import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { DuplicateMatch } from '../../api/endpoints.js';
import { Alert, StatusBadge, type Tone } from '../../components/ui/index.js';

/**
 * The clients that might already be this client.
 *
 * WHAT THIS COMPONENT IS FOR. Warning without blocking only works if the
 * warning is good enough to act on. A bare "possible duplicate found" teaches
 * the desk to click past it. So each match names the existing client, its
 * client ID, its contact details and — the part that decides it —
 * exactly WHICH field matched. "Same VAT registration number" and "similar
 * name" call for entirely different judgements, and only one of them is
 * usually a real duplicate.
 *
 * Each row links to the existing client file, opening in a new tab, so the desk
 * can look without losing the half-filled form behind it.
 */

function toneFor(confidence: string): Tone {
  switch (confidence) {
    case 'exact':
      return 'danger';
    case 'strong':
      return 'warning';
    default:
      return 'neutral';
  }
}

export interface DuplicateMatchListProps {
  readonly matches: readonly DuplicateMatch[];
  /** Shown when more candidates existed than could be compared. */
  readonly truncated?: boolean;
  /** Rendered per row, for the acknowledgement checkbox on the create form. */
  readonly renderAction?: (match: DuplicateMatch) => React.ReactNode;
}

export function DuplicateMatchList({
  matches,
  truncated = false,
  renderAction,
}: DuplicateMatchListProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-stack">
      {truncated ? (
        // Said plainly. "No duplicates found among the first 500" is a
        // different fact from "no duplicates exist", and the desk deserves the
        // difference (CLAUDE.md section 8).
        <Alert tone="warning" title={t('clients.duplicate.truncated')} />
      ) : null}

      <ul className="flex flex-col gap-stack">
        {matches.map((match) => (
          <li
            key={match.clientId}
            className="rounded-medium border border-default-200 bg-white p-4"
          >
            <div className="flex flex-wrap items-start gap-inline">
              <div className="min-w-0 flex-1">
                <p className="text-body font-medium text-default-900">
                  {match.name ?? t('common.notRecorded')}
                </p>
                <p className="text-caption text-default-600">
                  {match.clientNumber}
                </p>

                {match.contactEmail ? (
                  <p className="text-caption text-default-600" dir="ltr">
                    {match.contactEmail}
                  </p>
                ) : null}
                {match.contactPhone ? (
                  <p className="text-caption text-default-600" dir="ltr">
                    {match.contactPhone}
                  </p>
                ) : null}

                {/* WHY it matched. This is the part the desk decides on. */}
                <ul className="mt-2 flex flex-wrap gap-1">
                  {match.reasons.map((reason, index) => (
                    <li key={`${reason.field}-${reason.language ?? index}`}>
                      <StatusBadge
                        size="sm"
                        tone={toneFor(reason.confidence)}
                        label={t(`clients.duplicate.reason.${reason.field}`)}
                      />
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-col items-end gap-inline">
                <StatusBadge
                  tone={toneFor(match.confidence)}
                  label={t(`clients.duplicate.confidence.${match.confidence}`)}
                />
                {match.status === 'suspended' ? (
                  <StatusBadge
                    size="sm"
                    tone="neutral"
                    label={t('clients.status.suspended')}
                  />
                ) : null}
                <Link
                  to={`/clients/${match.clientId}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-caption text-primary-700 underline"
                >
                  {t('common.open')}
                </Link>
              </div>
            </div>

            {renderAction ? (
              <div className="mt-3 border-t border-default-100 pt-3">
                {renderAction(match)}
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
